import { useIntl } from 'react-intl';

import type { CountryCodeEnumType } from '@/types/regionTypes';

import { getUserTitleLabelByValue } from '@/components/FormFields/TitleField';
import { renderAddress, Summary } from '@/components/Summary';
import { Tooltip } from '@/components/Tooltip';
import type { CountryCode, OrderFragment } from '@/graphql/saleor/api';
import { useAddressValidationRules } from '@/hooks/useAddressValidationRules';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { commonMessages, EditDeliveryInfo } from '@/messages/common';
import * as OM from '@/messages/order';
import type { CheckoutState } from '@/states/checkout';

interface DeliverySummaryProps {
  data: CheckoutState<true>['checkout'] | OrderFragment;
  hasDeliverySchedule?: boolean;
  hideShippingName?: boolean;
  onEdit?: () => void;
  tooltip?: boolean;
}

export const DeliverySummary = ({
  data,
  hasDeliverySchedule = false,
  hideShippingName,
  onEdit,
  tooltip,
}: DeliverySummaryProps) => {
  const { formatMessage } = useIntl();
  const deliverySchedule = {
    header: formatMessage(commonMessages.deliverySchedule),
    val:
      data.deliveryMode === 'BOUTIQUE_PICKUP'
        ? formatMessage(
            commonMessages.deliveryScheduleDescriptionForBoutiqueDelivery,
          )
        : formatMessage(
            commonMessages.deliveryScheduleDescriptionForHomeDelivery,
          ),
  };
  const { useValidationRulesUpdate: useShippingValidationRulesUpdate } =
    useAddressValidationRules();
  const { useValidationRulesUpdate: useBillingValidationRulesUpdate } =
    useAddressValidationRules();
  useShippingValidationRulesUpdate(
    data.shippingAddress?.country.code as CountryCode,
  );
  useBillingValidationRulesUpdate(
    data.billingAddress?.country.code as CountryCode,
  );
  const shippingTitleValue =
    (data?.shippingAddress?.metadata
      ? getMetaValue(
          data.shippingAddress.metadata,
          ACCOUNT_METADATA.personalTitle,
        )
      : undefined) ?? data?.shippingTitle;
  const billingTitleValue =
    (data?.billingAddress?.metadata
      ? getMetaValue(
          data.billingAddress.metadata,
          ACCOUNT_METADATA.personalTitle,
        )
      : undefined) ?? data?.billingTitle;
  const shippingTitleLabel = getUserTitleLabelByValue(
    formatMessage,
    data?.shippingAddress?.country.code as CountryCodeEnumType | undefined,
    shippingTitleValue,
  );
  const billingTitleLabel = getUserTitleLabelByValue(
    formatMessage,
    data?.billingAddress?.country.code as CountryCodeEnumType | undefined,
    billingTitleValue,
  );

  return (
    <>
      {tooltip && (
        <Tooltip
          anchorSelect='[data-testid=sectionDelivery] [data-testid=iconEdit]'
          data-test-id='DeliverySummaryTooltip'
        >
          {EditDeliveryInfo}
        </Tooltip>
      )}
      <Summary data-test-id='DeliverySummary' name='delivery'>
        <Summary.Header data-test-id='DeliverySummaryHeader' onClick={onEdit}>
          {OM.DeliveryInformation}
        </Summary.Header>

        {data?.isShippingRequired &&
          renderAddress(
            shippingTitleLabel,
            hasDeliverySchedule,
            formatMessage(commonMessages.shippingAddress),
            data?.shippingAddress,
            deliverySchedule,
            hideShippingName,
          )}

        {renderAddress(
          billingTitleLabel,
          hasDeliverySchedule,
          formatMessage(commonMessages.billingAddress),
          data?.billingAddress,
        )}
      </Summary>
    </>
  );
};
